import 'react-loading-skeleton/dist/skeleton.css';

import { useTheme } from '@emotion/react';
import cn from 'classnames';
import { cloneElement, type MemoExoticComponent, memo, type ReactElement } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useMedia } from 'react-use';

import { ComponentSwitch, Truncate } from '@didaskill/ui/utils';

import { COLOR_GROUPS } from '../../../config/colors/constants';
import { Tooltip } from '../../general/tooltip/Tooltip';
import { Typography } from '../../general/typography/Typography';
import { TILE_VARIANTS } from './constants';
import * as styles from './Tile.styles';

export type TileProps = {
  elementRight?: ReactElement;
  shouldOverrideIconStyle?: boolean;
  icon?: ReactElement;
  title?: string;
  subtitle?: string;
  variant?: TILE_VARIANTS;
  iconVariant?: TILE_VARIANTS;
  isLoading?: boolean;
  isDisabled?: boolean;
  withShadow?: boolean;
  maxLinesTitle?: number | null;
  maxLinesSubtitle?: number | null;
  onClick?: () => void;
};

/**
 * A tile component that displays an icon, title, and subtitle with customizable styles and states.
 * It supports a loading state, disabled state, and multiple visual variants for flexible usage.
 *
 * ### Features
 * - Displays an icon with customizable colors based on the `variant` and `isDisabled` props.
 * - Shows a title and subtitle, or skeleton loaders if `isLoading` is true.
 * - Supports visual variants defined in `VARIANTS` for consistent styling.
 * - Adapts its appearance when `isDisabled` is true.
 *
 * @example
 * // Example usage of the Tile component
 * <Tile
 *   icon={<SomeIcon />}
 *   title="Dashboard"
 *   subtitle="Manage your settings"
 *   variant={Tile.VARIANTS.SECONDARY}
 *   isLoading={false}
 *   isDisabled={false}
 * />
 *
 * @param {Object} props - The props for the Tile component.
 * @param {ReactElement} [props.icon] - An optional icon element to display at the top of the tile.
 * @param {string} [props.title] - The main title text for the tile.
 * @param {string} [props.subtitle] - The subtitle text for the tile.
 * @param {VARIANTS} [props.variant=VARIANTS.PRIMARY] - The style variant of the tile (`PRIMARY`, `SECONDARY`, etc.).
 * @param {boolean} [props.isLoading=false] - Whether the tile is in a loading state, displaying skeletons instead of content.
 * @param {boolean} [props.isDisabled=false] - Whether the tile is disabled, affecting its appearance and interaction.
 *
 * @see {@link VARIANTS} for available style variants.
 * @see {@link Typography} for the typography options used in the component.
 */

export const TileBase = ({
  icon,
  title,
  subtitle,
  variant = TILE_VARIANTS.PRIMARY,
  iconVariant,
  shouldOverrideIconStyle = false,
  elementRight = null,
  isLoading = false,
  isDisabled = false,
  withShadow = true,
  maxLinesSubtitle = 1,
  maxLinesTitle = 1,
  onClick,
}: TileProps) => {
  const { colors, breakpoints } = useTheme();

  const activeIconVariant = iconVariant || variant;

  const isAboveMedium = useMedia(`(min-width: ${breakpoints.medium})`);

  const colorsGroup =
    isDisabled || variant === TILE_VARIANTS.CHROME ? COLOR_GROUPS.GRAY : variant.toLowerCase();

  const iconColorsGroup = isDisabled ? COLOR_GROUPS.GRAY : activeIconVariant.toLowerCase();

  const iconFillColors =
    isDisabled || [TILE_VARIANTS.CHROME, TILE_VARIANTS.DISCORD].includes(variant)
      ? [colors.gray[400], colors.gray[50]]
      : [colors[iconColorsGroup][500], colors[iconColorsGroup][50]];

  return (
    <button
      type="button"
      onClick={!isDisabled ? onClick : undefined}
      css={styles.container}
      className={cn({
        [variant]: !isDisabled,
        isDisabled,
        withShadow,
        isClickable: !!onClick,
      })}
    >
      {isLoading ? (
        <Skeleton height={54} width={54} baseColor={colors[colorsGroup][50]} />
      ) : (
        <ComponentSwitch value={shouldOverrideIconStyle}>
          <ComponentSwitch.Item value={false}>
            <div
              css={styles.iconContainer}
              className={cn({ [activeIconVariant]: !isDisabled, isDisabled })}
            >
              {cloneElement(icon, {
                size: isAboveMedium ? 24 : 18,
                fill: iconFillColors,
              })}
            </div>
          </ComponentSwitch.Item>

          <ComponentSwitch.Item value>{icon}</ComponentSwitch.Item>
        </ComponentSwitch>
      )}

      <div css={styles.textWithElementRight}>
        <div>
          {isLoading ? (
            <Skeleton height={30} width={120} baseColor={colors[colorsGroup][50]} />
          ) : (
            <Truncate maxLines={maxLinesTitle}>
              <Tooltip content={title} touch={false}>
                <Typography
                  variant={
                    isAboveMedium
                      ? Typography.VARIANTS.BODY_LARGE_BOLD
                      : Typography.VARIANTS.BODY_MEDIUM_BOLD
                  }
                >
                  {title}
                </Typography>
              </Tooltip>
            </Truncate>
          )}

          {isLoading ? (
            <Skeleton height={20} width={70} baseColor={colors[colorsGroup][50]} />
          ) : (
            <Truncate maxLines={maxLinesSubtitle}>
              <Tooltip content={subtitle} touch={false}>
                <Typography
                  variant={
                    isAboveMedium
                      ? Typography.VARIANTS.BODY_LARGE_REGULAR
                      : Typography.VARIANTS.BODY_SMALL_REGULAR
                  }
                >
                  {subtitle}
                </Typography>
              </Tooltip>
            </Truncate>
          )}
        </div>

        {elementRight}
      </div>
    </button>
  );
};

export const Tile: MemoExoticComponent<typeof TileBase> & {
  VARIANTS?: typeof TILE_VARIANTS;
} = memo(TileBase);

Tile.VARIANTS = TILE_VARIANTS;
